import type { NoteWithTemplateData } from '../noteTemplate';
import type {
  CreateStandalonePatientVisitRequest,
  CreateStandalonePatientVisitResponse,
  VideaNoteWriteBackStatus,
} from '../types/api';

import { paths } from './paths';
import type {
  VideaNotesApiAdapter,
  VideaNotesApiConfig,
} from './types/adapter';
import type {
  FeedbackBody,
  VoiceNoteTemplate,
  WritebackStatus,
} from './types/notes';

interface NoteWithWriteback {
  id: string;
  writebackStatus?: VideaNoteWriteBackStatus;
}

async function request<T>(
  config: VideaNotesApiConfig,
  path: string,
  options: {
    method: 'GET' | 'PATCH' | 'POST';
    body?: string;
    signal?: AbortSignal;
  },
): Promise<{ ok: true; data: T } | { ok: false; status: number }> {
  const { axios, getAuthHeaders, getBaseUrl, getContext, onAuthError } = config;
  const url = getBaseUrl().replace(/\/$/, '') + path;

  if (axios) {
    try {
      const { practiceId, organizationId } = getContext();
      const response = await axios.request<T>({
        url,
        method: options.method.toLowerCase() as 'get' | 'patch' | 'post',
        data: options.body,
        signal: options.signal,
        headers: {
          practice: practiceId,
          org: organizationId,
        },
      });
      return { ok: true, data: response.data };
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : 0;
      // Do not call config.onAuthError here: the axios instance's response
      // interceptor already invokes onAuthError on 401/403.
      return { ok: false, status: status ?? 0 };
    }
  }

  const { practiceId, organizationId } = getContext();
  const authHeaders = await getAuthHeaders();

  const headers: Record<string, string> = {
    ...authHeaders,
    practice: practiceId,
    org: organizationId,
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  try {
    const response = await fetch(url, {
      method: options.method,
      headers,
      body: options.body,
      signal: options.signal,
    });
    if (response.status === 401 || response.status === 403) {
      onAuthError?.();
      return { ok: false, status: response.status };
    }
    if (!response.ok) {
      return { ok: false, status: response.status };
    }
    const text = await response.text();
    let data: T;
    try {
      data = (text.trim() ? JSON.parse(text) : {}) as T;
    } catch {
      return { ok: false, status: 0 };
    }
    return { ok: true, data };
  } catch {
    return { ok: false, status: 0 };
  }
}

async function fetchNoteById(
  config: VideaNotesApiConfig,
  clinicalNoteId: string,
): Promise<NoteWithTemplateData | null> {
  if (!config.getContext().practiceId || !config.getContext().organizationId) {
    return null;
  }
  const result = await request<NoteWithTemplateData>(
    config,
    paths.videaNoteById(clinicalNoteId),
    { method: 'GET' },
  );
  return result.ok ? result.data : null;
}

async function patchNoteSummary(
  config: VideaNotesApiConfig,
  clinicalNoteId: string,
  body: Record<string, unknown>,
): Promise<boolean> {
  const { practiceId, organizationId } = config.getContext();
  if (!practiceId || !organizationId) {
    throw new Error('Practice ID and Organization ID are required');
  }
  const result = await request<unknown>(
    config,
    paths.videaNoteSummary(clinicalNoteId),
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
  if (result.ok) {
    return true;
  }

  if (result.status === 401 || result.status === 403) {
    throw new Error('Authentication error');
  }

  throw new Error(
    result.status > 0
      ? `Request failed with status ${result.status}`
      : 'Request failed',
  );
}

async function submitFeedback(
  config: VideaNotesApiConfig,
  clinicalNoteId: string,
  feedbackBody: FeedbackBody,
): Promise<boolean> {
  const { practiceId, organizationId } = config.getContext();
  if (!practiceId || !organizationId) {
    return false;
  }
  const result = await request<unknown>(
    config,
    paths.videaNoteFeedback(clinicalNoteId),
    {
      method: 'PATCH',
      body: JSON.stringify(feedbackBody),
    },
  );
  return result.ok;
}

async function getHasWriteback(config: VideaNotesApiConfig): Promise<boolean> {
  const { practiceId, organizationId } = config.getContext();
  if (!practiceId || !organizationId) {
    return false;
  }
  const result = await request<{ hasWritebackSupport: boolean }>(
    config,
    paths.videaNotesWriteback(),
    { method: 'GET' },
  );
  return result.ok ? result.data.hasWritebackSupport : false;
}

async function submitWriteback(
  config: VideaNotesApiConfig,
  clinicalNoteId: string,
  queryParams?: Record<string, string>,
): Promise<{ success: boolean; error?: string }> {
  const { practiceId, organizationId } = config.getContext();
  if (!practiceId || !organizationId) {
    return {
      success: false,
      error: 'Practice ID and Organization ID are required',
    };
  }
  let path = paths.videaNoteWriteback(clinicalNoteId);
  if (queryParams && Object.keys(queryParams).length > 0) {
    path += `?${new URLSearchParams(queryParams).toString()}`;
  }
  const result = await request<unknown>(config, path, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (result.ok) {
    return { success: true };
  }
  return {
    success: false,
    error:
      result.status > 0
        ? `Request failed with status ${result.status}`
        : 'Network error',
  };
}

async function getWritebackStatus(
  config: VideaNotesApiConfig,
  params:
    | { clinicalNoteId: string; unifiedAppointmentId: string }
    | { clinicalNoteId: string; standalonePatientVisitId: string },
  signal?: AbortSignal,
): Promise<WritebackStatus | undefined> {
  const { practiceId, organizationId } = config.getContext();
  if (!practiceId || !organizationId) {
    return undefined;
  }
  const path =
    'unifiedAppointmentId' in params
      ? paths.appointmentNotes(params.unifiedAppointmentId)
      : paths.standaloneVisitNotes(params.standalonePatientVisitId);
  const result = await request<NoteWithWriteback[]>(config, path, {
    method: 'GET',
    signal,
  });
  if (!result.ok) {
    return undefined;
  }
  const note = result.data.find((n) => n.id === params.clinicalNoteId);
  const ws = note?.writebackStatus;
  if (!ws) return undefined;
  return {
    clinicalNoteId: ws.clinicalNoteId,
    isProcessed: ws.isProcessed,
    processedAt: ws.processedAt,
    errorCode: ws.errorCode,
    errorMessage: ws.errorMessage,
    result: ws.result,
  };
}

async function fetchVoiceNoteTemplates(
  config: VideaNotesApiConfig,
  namespace?: string,
): Promise<VoiceNoteTemplate[]> {
  const { practiceId, organizationId } = config.getContext();
  if (!practiceId || !organizationId) {
    return [];
  }
  const result = await request<VoiceNoteTemplate[]>(
    config,
    paths.voiceNoteTemplates(namespace),
    { method: 'GET' },
  );
  return result.ok ? result.data : [];
}

async function createStandalonePatientVisit(
  config: VideaNotesApiConfig,
  data: CreateStandalonePatientVisitRequest,
): Promise<CreateStandalonePatientVisitResponse | null> {
  const { practiceId, organizationId } = config.getContext();
  if (!practiceId || !organizationId) {
    config.onAuthError?.();
    return null;
  }
  const result = await request<CreateStandalonePatientVisitResponse>(
    config,
    paths.standalonePatientVisits(),
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
  if (result.ok) return result.data;
  // Throw so callers can catch and show error / auth handling (isAuthError checks message for 401/403)
  const message =
    result.status === 401 || result.status === 403
      ? `${result.status}`
      : result.status > 0
      ? `Request failed with status ${result.status}`
      : 'Request failed';
  throw new Error(message);
}

export function createVideaNotesApiClient(
  config: VideaNotesApiConfig,
): VideaNotesApiAdapter {
  return {
    ...config,
    fetchNoteById: (clinicalNoteId) => fetchNoteById(config, clinicalNoteId),
    patchNoteSummary: (clinicalNoteId, body) =>
      patchNoteSummary(config, clinicalNoteId, body),
    submitFeedback: (clinicalNoteId, feedbackBody) =>
      submitFeedback(config, clinicalNoteId, feedbackBody),
    getHasWriteback: () => getHasWriteback(config),
    submitWriteback: (clinicalNoteId, queryParams) =>
      submitWriteback(config, clinicalNoteId, queryParams),
    getWritebackStatus: (params, signal) =>
      getWritebackStatus(config, params, signal),
    fetchVoiceNoteTemplates: (namespace) =>
      fetchVoiceNoteTemplates(config, namespace),
    createStandalonePatientVisit: (data) =>
      createStandalonePatientVisit(config, data),
  };
}
